import { Clock, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import { useConsultation } from "@/components/ConsultationModal";

const CARDS = [
  { icon: Mail, title: "Email us", lines: ["hello@techaxisltd.co.uk", "Replies within one business day"], testid: "contact-card-email" },
  { icon: Phone, title: "Call us", lines: ["+44 7358 002510", "Mon–Fri, 9:00–18:00 GMT"], testid: "contact-card-phone" },
  { icon: MapPin, title: "Visit us", lines: ["861b Stockport Road", "Manchester M19 3PW, UK"], testid: "contact-card-office" },
  { icon: ShieldCheck, title: "Support & emergencies", lines: ["support@techaxisltd.co.uk", "24/7 for managed clients"], testid: "contact-card-emergency" },
];

export default function Contact() {
  const { open } = useConsultation();
  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden pt-32 pb-12 lg:pt-40">
        <div className="absolute inset-0 hero-grid opacity-60" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,102,255,0.15),transparent_60%)]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">Contact us</p>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl" data-testid="contact-page-title">
              Tell us what you're <span className="text-gradient">building</span>
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">
              Send an enquiry and you'll receive a confirmation email immediately, with a personal reply from
              our team within one business day. Prefer to talk?{" "}
              <button type="button" onClick={open} className="font-semibold text-cyan-300 underline-offset-4 hover:underline" data-testid="contact-consultation-link">
                Book a free 30-minute consultation
              </button>
              .
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr]">
          <Reveal>
            <div className="glass rounded-2xl p-7 sm:p-9">
              <ContactForm />
            </div>
          </Reveal>
          <div className="space-y-5">
            {CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.07}>
                <div className="card-glow flex items-start gap-4 rounded-2xl border border-white/10 bg-navy-900/60 p-5" data-testid={c.testid}>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-cyan-400/20 bg-cyan-400/5">
                    <c.icon className="h-5 w-5 text-cyan-300" />
                  </div>
                  <div>
                    <h3 className="font-display text-base font-semibold text-white">{c.title}</h3>
                    {c.lines.map((l) => (
                      <p key={l} className="mt-0.5 text-sm text-slate-400">{l}</p>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="flex items-center gap-3 rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-5" data-testid="contact-response-promise">
                <Clock className="h-5 w-5 shrink-0 text-cyan-300" />
                <p className="text-sm text-slate-300">
                  Every enquiry is read by a real person on our team — never a sales queue.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
